import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const SEV_CONFIG = {
  CRITICAL: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', dot: '#dc2626', rank: 1 },
  HIGH:     { bg: '#fff7ed', border: '#fdba74', text: '#9a3412', dot: '#ea580c', rank: 2 },
  MEDIUM:   { bg: '#fefce8', border: '#fde047', text: '#854d0e', dot: '#ca8a04', rank: 3 },
  LOW:      { bg: '#f0f9ff', border: '#7dd3fc', text: '#0c4a6e', dot: '#0284c7', rank: 4 },
};

function SevBadge({ severity }) {
  const c = SEV_CONFIG[severity] || { bg: '#f8fafc', text: '#64748b', dot: '#94a3b8' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 9px', borderRadius: 12, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.text, border: `1px solid ${c.border || '#e2e8f0'}` }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot }} />
      {severity || 'UNKNOWN'}
    </span>
  );
}

function SnoozeModal({ alert, onClose, onSnoozed }) {
  const { t } = useTranslation();
  const [until, setUntil] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minISO = minDate.toISOString().slice(0, 10);

  const handleSnooze = async () => {
    if (!until) { setErr(t('CommandCenter.selectDate')); return; }
    setSaving(true); setErr('');
    try {
      await axiosInstance.post(`/partner/alerts/${alert.id}/snooze`, {
        findingType: 'CVE',
        until: until + 'T00:00:00',
      });
      onSnoozed();
    } catch { setErr(t('CommandCenter.failedToSnooze')); }
    finally { setSaving(false); }
  };

  return (
    <div className="mp-overlay" onClick={onClose}>
      <div className="mp-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div className="mp-modal-header">
          <h3><i className="fas fa-bell-slash" /> {t('CommandCenter.snoozeAlert')}</h3>
          <button onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="mp-modal-body">
          {err && <div className="mp-msg mp-msg-err">{err}</div>}
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 12 }}>
            {t('CommandCenter.snoozePrefix')} <strong>{alert.cveId}</strong> {t('CommandCenter.snoozeOn')} <em>{alert.orgName}</em> {t('CommandCenter.snoozeUntil')}
          </p>
          <input type="date" className="mp-input" min={minISO}
            value={until} onChange={e => setUntil(e.target.value)} />
        </div>
        <div className="mp-modal-footer">
          <button className="mp-btn-secondary" onClick={onClose}>{t('CommandCenter.cancel')}</button>
          <button className="mp-btn-primary" onClick={handleSnooze} disabled={saving || !until}>
            {saving ? <><i className="fas fa-spinner fa-spin" /> {t('CommandCenter.snoozing')}</> : <><i className="fas fa-bell-slash" /> {t('CommandCenter.snooze')}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignModal({ alert, onClose, onAssigned }) {
  const { t } = useTranslation();
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleAssign = async () => {
    setSaving(true); setErr('');
    try {
      await axiosInstance.post(`/partner/alerts/${alert.id}/assign`, {
        dueDate: dueDate || null,
        projectId: null,
      });
      onAssigned();
    } catch (e) { setErr(e?.response?.data?.message || t('CommandCenter.failedToAssign')); }
    finally { setSaving(false); }
  };

  return (
    <div className="mp-overlay" onClick={onClose}>
      <div className="mp-modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="mp-modal-header">
          <h3><i className="fas fa-tasks" /> {t('CommandCenter.createRemediationTicket')}</h3>
          <button onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="mp-modal-body">
          {err && <div className="mp-msg mp-msg-err">{err}</div>}
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.85rem' }}>
            <div><strong>{alert.cveId}</strong> <SevBadge severity={alert.severity} /></div>
            <div style={{ color: '#64748b', marginTop: 4 }}>{alert.orgName} · {alert.domainName}</div>
          </div>
          <div className="mp-field">
            <label>{t('CommandCenter.dueDateOptional')}</label>
            <input type="date" className="mp-input" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </div>
          <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 8 }}>
            {t('CommandCenter.autoCreateProjectNote')}
          </p>
        </div>
        <div className="mp-modal-footer">
          <button className="mp-btn-secondary" onClick={onClose}>{t('CommandCenter.cancel')}</button>
          <button className="mp-btn-primary" onClick={handleAssign} disabled={saving}>
            {saving ? <><i className="fas fa-spinner fa-spin" /> {t('CommandCenter.creating')}</> : <><i className="fas fa-plus" /> {t('CommandCenter.createTicket')}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CommandCenter() {
  const { t } = useTranslation();
  const [alerts, setAlerts]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [msg, setMsg]             = useState('');
  const [filterSev, setFilterSev] = useState('ALL');
  const [filterDays, setFilterDays] = useState(30);
  const [snoozeAlert, setSnoozeAlert] = useState(null);
  const [assignAlert, setAssignAlert] = useState(null);
  const [showSnoozed, setShowSnoozed] = useState(false);

  const fetchAlerts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterSev !== 'ALL') params.append('severity', filterSev);
    if (filterDays) params.append('ageDays', filterDays);
    axiosInstance.get(`/partner/alerts?${params.toString()}`)
      .then(r => setAlerts(r.data || []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  }, [filterSev, filterDays]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const visibleAlerts = alerts.filter(a => showSnoozed || !a.snoozedUntil);

  const counts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
  alerts.forEach(a => { if (!a.snoozedUntil && counts[a.severity] !== undefined) counts[a.severity]++; });

  return (
    <div>
      {/* KPI strip */}
      <div className="mp-kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(sev => {
          const c = SEV_CONFIG[sev];
          return (
            <div key={sev} className="mp-kpi" style={{ cursor: 'pointer', borderLeft: `3px solid ${c.dot}`,
              background: counts[sev] > 0 ? c.bg : undefined }}
              onClick={() => setFilterSev(filterSev === sev ? 'ALL' : sev)}>
              <div className="mp-kpi-label" style={{ color: c.text }}>{t(`CommandCenter.sev_${sev}`)}</div>
              <div className="mp-kpi-value" style={{ color: c.dot, fontSize: '1.8rem' }}>{counts[sev]}</div>
            </div>
          );
        })}
      </div>

      {msg && (
        <div className={`mp-msg ${msg.toLowerCase().includes('fail') ? 'mp-msg-err' : ''}`}
          style={{ marginBottom: 12 }}>{msg}</div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(s => (
            <button key={s}
              style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: filterSev === s ? '#1e3a5f' : '#f1f5f9',
                color: filterSev === s ? '#fff' : '#475569',
                border: '1px solid ' + (filterSev === s ? '#1e3a5f' : '#e2e8f0') }}
              onClick={() => setFilterSev(s)}>{t(`CommandCenter.sev_${s}`)}</button>
          ))}
        </div>
        <select value={filterDays} onChange={e => setFilterDays(Number(e.target.value))}
          style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, border: '1px solid #e2e8f0', background: '#fff' }}>
          <option value={7}>{t('CommandCenter.last7Days')}</option>
          <option value={30}>{t('CommandCenter.last30Days')}</option>
          <option value={90}>{t('CommandCenter.last90Days')}</option>
          <option value={0}>{t('CommandCenter.allTime')}</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
          <input type="checkbox" checked={showSnoozed} onChange={e => setShowSnoozed(e.target.checked)} />
          {t('CommandCenter.showSnoozed')}
        </label>
        <button className="mp-btn-secondary" style={{ marginLeft: 'auto', fontSize: 12 }}
          onClick={fetchAlerts}>
          <i className="fas fa-sync" /> {t('CommandCenter.refresh')}
        </button>
      </div>

      {/* Alert table */}
      {loading ? (
        <div className="mp-loading"><i className="fas fa-spinner fa-spin" /> {t('CommandCenter.loadingAlerts')}</div>
      ) : visibleAlerts.length === 0 ? (
        <div className="mp-empty">
          <i className="fas fa-shield-alt" style={{ fontSize: 32, marginBottom: 8, display: 'block', color: '#94a3b8' }} />
          {alerts.length === 0
            ? t('CommandCenter.emptyNoAlerts')
            : t('CommandCenter.emptyAllSnoozed')}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="mp-table">
            <thead>
              <tr>
                <th>{t('CommandCenter.colSeverity')}</th>
                <th>{t('CommandCenter.colCveId')}</th>
                <th>{t('CommandCenter.colClientOrg')}</th>
                <th>{t('CommandCenter.colDomain')}</th>
                <th>{t('CommandCenter.colApplication')}</th>
                <th>{t('CommandCenter.colDescription')}</th>
                <th>{t('CommandCenter.colLastScan')}</th>
                <th>{t('CommandCenter.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleAlerts.map(a => {
                const isSnoozed = !!a.snoozedUntil;
                return (
                  <tr key={a.id} style={{ opacity: isSnoozed ? 0.5 : 1 }}>
                    <td><SevBadge severity={a.severity} /></td>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>
                        {a.cveId}
                      </span>
                      {a.exploitabilityScore && (
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                          {t('CommandCenter.cvss', { score: Number(a.exploitabilityScore).toFixed(1) })}
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.83rem' }}>{a.orgName}</span>
                    </td>
                    <td style={{ fontSize: '0.8rem', color: '#475569' }}>
                      <span>{a.domainName}</span>
                      {a.subdomainName && a.subdomainName !== a.domainName && (
                        <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{a.subdomainName}</div>
                      )}
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>
                      <div>{a.applicationName || '—'}</div>
                      {a.version && <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>v{a.version}</div>}
                    </td>
                    <td style={{ maxWidth: 260, fontSize: '0.8rem', color: '#64748b' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}
                        title={a.description}>{a.description || '—'}</div>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                      {a.lastScanDate ? new Date(a.lastScanDate).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {!isSnoozed && (
                          <>
                            <button className="mp-btn-sm" title={t('CommandCenter.createRemediationTicketTitle')}
                              onClick={() => setAssignAlert(a)}>
                              <i className="fas fa-tasks" />
                            </button>
                            <button className="mp-btn-sm mp-btn-sm-outline" title={t('CommandCenter.snoozeThisAlert')}
                              onClick={() => setSnoozeAlert(a)}>
                              <i className="fas fa-bell-slash" />
                            </button>
                          </>
                        )}
                        {isSnoozed && (
                          <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                            {t('CommandCenter.snoozedUntilDate', { date: new Date(a.snoozedUntil).toLocaleDateString() })}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {snoozeAlert && (
        <SnoozeModal
          alert={snoozeAlert}
          onClose={() => setSnoozeAlert(null)}
          onSnoozed={() => {
            setSnoozeAlert(null);
            setMsg(t('CommandCenter.toastAlertSnoozed'));
            fetchAlerts();
          }}
        />
      )}
      {assignAlert && (
        <AssignModal
          alert={assignAlert}
          onClose={() => setAssignAlert(null)}
          onAssigned={() => {
            setAssignAlert(null);
            setMsg(t('CommandCenter.toastTicketCreated'));
            fetchAlerts();
          }}
        />
      )}
    </div>
  );
}
